import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import StateModal from './StateModal';
import { showSuccess, showError } from '../../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface State {
  state_id: number;
  state_name: string;
  state_status: number;
}

const States: React.FC = () => {
  const [states, setStates] = useState<State[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<State | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Cancelled');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchStates = async () => {
    try {
      const data = await apiRequest<State[]>({
        method: 'get',
        url: 'state',
      });
      setStates(data);
    } catch (error) {
      showError('Failed to fetch states');
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  const handleEditClick = (record: State) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchStates();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (state_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-state/${state_id}`,
      });
      showSuccess('State deleted');
      fetchStates();
    } catch {
      showError('Failed to delete state');
    }
  };

  const handleStatusToggle = async (state_id: number) => {
    setStates((prev) =>
      prev.map((item) =>
        item.state_id === state_id
          ? { ...item, state_status: item.state_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-state/${state_id}`,
      });
      showSuccess('State status updated');
    } catch {
      showError('Failed to update status');
      fetchStates();
    }
  };

  const handleTableChange: TableProps<State>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<State> = [
    {
      title: 'State Name',
      dataIndex: 'state_name',
      key: 'state_name',
      filters: Array.from(new Set(states.map((s) => s.state_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filteredValue: filteredInfo.state_name || null,
      onFilter: (value, record) => record.state_name.includes(value as string),
      sorter: (a, b) => a.state_name.localeCompare(b.state_name),
      sortOrder: sortedInfo.columnKey === 'state_name' ? sortedInfo.order : null,
      ellipsis: true,
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <Space>
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checked={!!record.state_status}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                onChange={() => handleStatusToggle(record.state_id)}
              />
              <Button onClick={() => handleEditClick(record)}>
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this state?"
              onConfirm={() => handleDelete(record.state_id)}
              onCancel={cancel}
            >
              <Button danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View States</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add State
          </Button>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<State>
          className={styles.customTable}
          columns={columns}
          rowKey="state_id"
          // Slice data based on current page and pageSize
          dataSource={states.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          pagination={false} // Disable built-in Ant Design pagination
          bordered
          footer={() => (
            <Flex justify="space-between" align="center">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, states.length)} of {states.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={states.length}
                showSizeChanger
                pageSizeOptions={['5', '10', '20', '50']}
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize: pageSize || pagination.pageSize })
                }
              />
            </Flex>
          )}
        />
      </div>

      <StateModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default States;
